import { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "@/components/Reveal";
import api from "@/lib/api";
import { SITE, waLink } from "@/lib/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you within a few hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-white border border-border rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-sun/50 focus:border-sun transition-all";

  const INFO = [
    { icon: Phone, label: "Call us", value: SITE.phoneDisplay, href: `tel:${SITE.phoneRaw}`, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat now — replies in minutes", href: waLink("Hi click2trip!"), testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email" },
    { icon: MapPin, label: "Visit us", value: SITE.address, testid: "contact-address" },
    { icon: Clock, label: "Business hours", value: SITE.hours, testid: "contact-hours" },
  ];

  return (
    <div className="pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHeading overline="Contact" title={<>Let's plan it <span className="font-light italic">together.</span></>} className="mb-16" />
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {INFO.map((item, i) => (
              <Reveal key={item.label} delay={i * 0.06}>
                {item.href ? (
                  <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                    data-testid={item.testid}
                    className="flex items-start gap-4 bg-white border border-border rounded-2xl p-5 hover:border-sun transition-colors group">
                    <div className="w-11 h-11 rounded-xl bg-sand/70 flex items-center justify-center shrink-0 group-hover:bg-sun transition-colors">
                      <item.icon size={19} className="text-ocean group-hover:text-bone transition-colors" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-ink/40 mb-0.5">{item.label}</p>
                      <p className="font-medium text-sm">{item.value}</p>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-start gap-4 bg-white border border-border rounded-2xl p-5" data-testid={item.testid}>
                    <div className="w-11 h-11 rounded-xl bg-sand/70 flex items-center justify-center shrink-0">
                      <item.icon size={19} className="text-ocean" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-ink/40 mb-0.5">{item.label}</p>
                      <p className="font-medium text-sm">{item.value}</p>
                    </div>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} className="bg-white border border-border rounded-3xl p-8 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={input} data-testid="contact-name-input" />
                <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={input} data-testid="contact-email-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={input} data-testid="contact-phone-input" />
                <input placeholder="Subject" value={form.subject} onChange={set("subject")} className={input} data-testid="contact-subject-input" />
              </div>
              <textarea required rows={5} placeholder="Tell us about your trip or question *" value={form.message} onChange={set("message")}
                className={`${input} resize-none`} data-testid="contact-message-input" />
              <button type="submit" disabled={loading} data-testid="contact-submit-btn"
                className="inline-flex items-center gap-2 bg-sun text-bone font-semibold px-9 py-3.5 rounded-full hover:bg-[#e85a28] hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-60">
                {loading ? "Sending..." : <><Send size={15} /> Send Message</>}
              </button>
            </form>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="mt-12">
          <div className="rounded-3xl overflow-hidden border border-border h-80" data-testid="contact-map">
            <iframe title="click2trip office location — Limda Chowk, Rajkot" src="https://www.google.com/maps?q=Premji's+Park,+Limda+Chowk,+Rajkot,+Gujarat+360001&output=embed"
              className="w-full h-full" loading="lazy" />
          </div>
        </Reveal>
      </div>
    </div>
  );
}
